// Public signup flow (M9.2).
//
// Apex auth URLs (separate Clerk path prefixes — avoids remount bug):
//   /signup        — Clerk SignUp (new workspace)
//   /signin        — Clerk SignIn (returning users; marketing header)
//   /signup/company — signed in, no org yet; company form → POST /api/signup
//
// Signed-in users with a Clerk org redirect to https://<slug>.solvait.ai/mission-control.

import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  useAuth,
  useClerk,
  useUser,
} from "@clerk/clerk-react";
import { Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, Route, Routes } from "react-router-dom";

import { LtrText } from "../components/ui/LtrNumber";
import { syncLocaleFromUrl } from "../i18n";
import { api, setTokenProvider } from "../lib/api";
import type { SignupSlugCheck } from "../lib/api";
import {
  signupAuthStepUrl,
  signupCompanyStepUrl,
  signupLocaleSearch,
  signupSigninStepUrl,
} from "../lib/signup-urls";

export type SignupFlow = "signup" | "signin";

const clerkAppearance = {
  elements: {
    rootBox: "shadow-none",
    card: "shadow-none border-none",
    formButtonPrimary: "bg-brand hover:opacity-90",
    footerAction: "hidden",
  },
};

export function SignupPage({ flow = "signup" }: { flow?: SignupFlow }) {
  const { t } = useTranslation();

  useEffect(() => {
    void syncLocaleFromUrl();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-gradient text-white">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="leading-tight">
            <p className="text-base font-semibold">{t("app.title")}</p>
            <p className="text-xs text-slate-500">
              {t("signup.header_brand_tagline")}
            </p>
          </div>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Routes>
            {flow === "signup" ? (
              <>
                <Route path="company" element={<CompanyStep />} />
                <Route index element={<SignupAuthStep />} />
                {/* Clerk SSO / factor subpaths stay under /signup/* */}
                <Route path="*" element={<SignupAuthStep />} />
              </>
            ) : (
              <>
                <Route index element={<SigninAuthStep />} />
                <Route path="*" element={<SigninAuthStep />} />
              </>
            )}
          </Routes>
        </div>
      </main>
    </div>
  );
}

function AuthStepLoading() {
  const { t } = useTranslation();
  return (
    <p className="text-center text-sm text-slate-500" role="status">
      {t("common.loading", "Loading…")}
    </p>
  );
}

/** First org membership slug, if any. */
function primaryTenantSlug(
  user: ReturnType<typeof useUser>["user"],
): string | null {
  const memberships = user?.organizationMemberships ?? [];
  return memberships.length > 0 ? memberships[0].organization.slug : null;
}

/**
 * Sign-in flow only: existing org → tenant MC; otherwise company step.
 * Not used on /signup — Create workspace must stay on the signup UI.
 */
function SigninPostAuthRedirect() {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();

  const tenantSlug = primaryTenantSlug(user);

  useEffect(() => {
    if (!isLoaded || !userLoaded || !isSignedIn || !tenantSlug) return;
    window.location.assign(
      `https://${tenantSlug}.solvait.ai/mission-control`,
    );
  }, [isLoaded, userLoaded, isSignedIn, tenantSlug]);

  if (!isLoaded || !userLoaded) return <AuthStepLoading />;
  if (!isSignedIn || !user) return null;

  if (tenantSlug) return <AuthStepLoading />;

  return <Navigate to={`/signup/company${signupLocaleSearch()}`} replace />;
}

/** /signup: resume company step if signed in without org; never auto-send to tenant. */
function SignupSignedInGate() {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();

  if (!isLoaded || !userLoaded) return <AuthStepLoading />;
  if (!isSignedIn || !user) return null;

  const slug = primaryTenantSlug(user);
  if (!slug) {
    return <Navigate to={`/signup/company${signupLocaleSearch()}`} replace />;
  }

  return <SignupAlreadyHasWorkspace slug={slug} />;
}

function SignupAlreadyHasWorkspace({ slug }: { slug: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 text-center">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("signup.hero_title_signup")}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {t(
            "signup.already_has_workspace",
            "You're signed in to an existing workspace. Open it below, or sign in with a different account to create a new company.",
          )}
        </p>
      </header>
      <a
        href={`https://${slug}.solvait.ai/mission-control`}
        className="inline-flex w-full justify-center rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-brand-fg transition hover:opacity-90"
      >
        {t("signup.open_workspace", "Open {{slug}}.solvait.ai", { slug })}
      </a>
      <p className="text-sm text-slate-600">
        <Link
          to={`/signin${signupLocaleSearch()}`}
          className="font-medium text-slate-900 underline-offset-4 hover:underline"
        >
          {t("signup.sign_in_different", "Sign in with a different account")}
        </Link>
      </p>
    </div>
  );
}

function SignupAuthStep() {
  const { isLoaded } = useAuth();
  if (!isLoaded) return <AuthStepLoading />;

  return (
    <>
      <SignedIn>
        <SignupSignedInGate />
      </SignedIn>
      <SignedOut>
        <SignupAuthCard />
      </SignedOut>
    </>
  );
}

function SigninAuthStep() {
  const { isLoaded } = useAuth();
  if (!isLoaded) return <AuthStepLoading />;

  return (
    <>
      <SignedIn>
        <SigninPostAuthRedirect />
      </SignedIn>
      <SignedOut>
        <SigninAuthCard />
      </SignedOut>
    </>
  );
}

function SignupAuthCard() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <header className="text-center">
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("signup.hero_title_signup")}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {t("signup.hero_sub_signup")}
        </p>
      </header>
      <div className="rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
        <SignUp
          routing="path"
          path="/signup"
          signInUrl={signupSigninStepUrl()}
          forceRedirectUrl={signupCompanyStepUrl()}
          fallbackRedirectUrl={signupCompanyStepUrl()}
          signInForceRedirectUrl={signupCompanyStepUrl()}
          appearance={clerkAppearance}
        />
      </div>
      <p className="text-center text-sm text-slate-600">
        {t("signup.have_account")}{" "}
        <Link
          to={`/signin${signupLocaleSearch()}`}
          className="font-medium text-slate-900 underline-offset-4 hover:underline"
        >
          {t("signup.sign_in")}
        </Link>
      </p>
    </div>
  );
}

function SigninAuthCard() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <header className="text-center">
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("signup.hero_title_signin")}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {t("signup.hero_sub_signin")}
        </p>
      </header>
      <div className="rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
        <SignIn
          routing="path"
          path="/signin"
          signUpUrl={signupAuthStepUrl()}
          forceRedirectUrl={signupCompanyStepUrl()}
          fallbackRedirectUrl={signupCompanyStepUrl()}
          signUpForceRedirectUrl={signupAuthStepUrl()}
          appearance={clerkAppearance}
        />
      </div>
      <p className="text-center text-sm text-slate-600">
        {t("signup.need_workspace")}{" "}
        <Link
          to={`/signup${signupLocaleSearch()}`}
          className="font-medium text-slate-900 underline-offset-4 hover:underline"
        >
          {t("signup.sign_up")}
        </Link>
      </p>
    </div>
  );
}

/** Company step: org members belong on their tenant, not a second workspace form. */
function CompanySignedInGate() {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();
  const tenantSlug = primaryTenantSlug(user);

  useEffect(() => {
    if (!isLoaded || !userLoaded || !isSignedIn || !tenantSlug) return;
    window.location.assign(
      `https://${tenantSlug}.solvait.ai/mission-control`,
    );
  }, [isLoaded, userLoaded, isSignedIn, tenantSlug]);

  if (!isLoaded || !userLoaded) return <AuthStepLoading />;
  if (!isSignedIn || !user) return null;
  if (tenantSlug) return <AuthStepLoading />;

  return <CompanyFormInner />;
}

function CompanyStep() {
  return (
    <>
      <SignedOut>
        <Navigate to={`/signup${signupLocaleSearch()}`} replace />
      </SignedOut>
      <SignedIn>
        <CompanySignedInGate />
      </SignedIn>
    </>
  );
}

function CompanyFormInner() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const clerk = useClerk();

  useEffect(() => {
    setTokenProvider(() => getToken());
    return () => setTokenProvider(null);
  }, [getToken]);

  const [companyEn, setCompanyEn] = useState("");
  const [companyAr, setCompanyAr] = useState("");
  const [slug, setSlug] = useState("");
  const [slugUserEdited, setSlugUserEdited] = useState(false);
  const [slugCheck, setSlugCheck] = useState<SignupSlugCheck | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const slugDebounce = useRef<number | null>(null);

  const [pollingTenantId, setPollingTenantId] = useState<string | null>(null);
  const [pollSecondsElapsed, setPollSecondsElapsed] = useState(0);
  const [pollProgress, setPollProgress] = useState<string>(
    "Preparing your workspace",
  );

  function suggestSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32);
  }

  useEffect(() => {
    if (!slugUserEdited) {
      setSlug(suggestSlug(companyEn));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyEn]);

  useEffect(() => {
    if (slugDebounce.current !== null) {
      window.clearTimeout(slugDebounce.current);
    }
    if (!slug || slug.length < 3) {
      setSlugCheck(null);
      return;
    }
    setSlugChecking(true);
    slugDebounce.current = window.setTimeout(async () => {
      try {
        const result = await api.checkSignupSlug(slug);
        setSlugCheck(result);
      } catch (e) {
        setSlugCheck({
          slug,
          available: false,
          reason: (e as Error).message,
        });
      } finally {
        setSlugChecking(false);
      }
    }, 300);
    return () => {
      if (slugDebounce.current !== null) {
        window.clearTimeout(slugDebounce.current);
      }
    };
  }, [slug]);

  const canSubmit =
    !submitting &&
    !slugChecking &&
    slugCheck?.available === true &&
    companyEn.trim().length > 0 &&
    companyAr.trim().length > 0;

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;
      setSubmitting(true);
      setSubmitError(null);
      try {
        const accepted = await api.signup({
          slug,
          company_name_en: companyEn.trim(),
          company_name_ar: companyAr.trim(),
        });
        setPollingTenantId(accepted.tenant_id);
      } catch (e) {
        setSubmitError((e as Error).message);
        setSubmitting(false);
      }
    },
    [canSubmit, slug, companyEn, companyAr],
  );

  useEffect(() => {
    if (!pollingTenantId) return;
    let cancelled = false;
    const startedAt = Date.now();
    const POLL_MS = 3000;
    const MAX_POLL_MS = 5 * 60_000;

    async function tick() {
      if (cancelled) return;
      setPollSecondsElapsed(Math.floor((Date.now() - startedAt) / 1000));
      try {
        const result = await api.signupStatus(pollingTenantId!);
        if (cancelled) return;
        if (result.progress) {
          setPollProgress(result.progress);
        }
        if (result.status === "ready" && result.redirect_url) {
          if (result.clerk_org_id) {
            try {
              await clerk.setActive({
                organization: result.clerk_org_id,
              });
            } catch (e) {
              console.warn("setActive failed:", e);
            }
          }
          window.location.assign(`${result.redirect_url}/onboarding/welcome`);
          return;
        }
        if (result.status === "failed") {
          setSubmitError(
            result.error ??
              "Signup failed for an unknown reason. Please try again.",
          );
          setPollingTenantId(null);
          setSubmitting(false);
          return;
        }
      } catch (e) {
        if (Date.now() - startedAt > MAX_POLL_MS) {
          setSubmitError(
            (e as Error).message ?? "Couldn't reach the status endpoint.",
          );
          setPollingTenantId(null);
          setSubmitting(false);
          return;
        }
      }
      if (Date.now() - startedAt > MAX_POLL_MS) {
        setSubmitError(
          "Signup is taking longer than expected. Your workspace may " +
            "still be provisioning in the background — try visiting " +
            `https://${slug}.solvait.ai in a few minutes.`,
        );
        setPollingTenantId(null);
        setSubmitting(false);
        return;
      }
      window.setTimeout(tick, POLL_MS);
    }

    tick();
    return () => {
      cancelled = true;
    };
  }, [pollingTenantId, slug, clerk]);

  if (pollingTenantId) {
    return (
      <div className="space-y-6">
        <header className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900">
            Setting up your workspace…
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            We're provisioning <span className="font-mono">{slug}.solvait.ai</span>.
            This usually takes 60–90 seconds. Don't close this tab.
          </p>
        </header>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-block h-3 w-3 animate-pulse rounded-full bg-emerald-500"
            />
            <span className="text-sm text-slate-700">
              {pollProgress}
              {pollSecondsElapsed > 0 && (
                <span className="ms-2 text-xs text-slate-500">
                  ({pollSecondsElapsed}s elapsed)
                </span>
              )}
            </span>
          </div>
          {pollSecondsElapsed > 30 && (
            <p className="mt-4 text-xs text-slate-500">
              Taking longer than expected? Your workspace may have already
              finished provisioning —{" "}
              <a
                className="font-medium text-slate-900 underline-offset-4 hover:underline"
                href={`https://${slug}.solvait.ai/onboarding/welcome`}
              >
                try visiting it
              </a>
              .
            </p>
          )}
          {submitError && (
            <p className="mt-4 text-sm text-rose-700">{submitError}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="text-center">
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("signup.company_step_title")}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {t("signup.company_step_sub")}
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      >
        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            {t("signup.company_name_en")}
          </span>
          <input
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:outline-none"
            value={companyEn}
            onChange={(e) => setCompanyEn(e.target.value)}
            placeholder={t("signup.company_name_en_placeholder")}
            required
            autoFocus
            maxLength={255}
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            {t("signup.company_name_ar_label")}
          </span>
          <input
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:outline-none"
            value={companyAr}
            onChange={(e) => setCompanyAr(e.target.value)}
            placeholder={t("signup.company_name_ar_placeholder")}
            required
            dir="rtl"
            maxLength={255}
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            {t("signup.url_slug")}
          </span>
          <div className="mt-1 flex items-center rounded-md border border-slate-300 shadow-sm focus-within:border-slate-900">
            <span className="flex-shrink-0 select-none px-3 text-sm text-slate-500">
              <LtrText>https://</LtrText>
            </span>
            <input
              className="min-w-0 flex-1 border-0 px-0 py-2 text-sm focus:outline-none focus:ring-0"
              value={slug}
              onChange={(e) => {
                setSlugUserEdited(true);
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "")
                    .slice(0, 32),
                );
              }}
              required
              minLength={3}
              maxLength={32}
              pattern="^[a-z0-9](?:[a-z0-9\-]{1,30}[a-z0-9])?$"
              dir="ltr"
            />
            <span className="flex-shrink-0 select-none px-3 text-sm text-slate-500">
              <LtrText>.solvait.ai</LtrText>
            </span>
          </div>
          <SlugStatus checking={slugChecking} check={slugCheck} />
        </label>

        {submitError && (
          <p className="text-sm text-rose-700">{submitError}</p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-brand-fg transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting
            ? t("signup.creating")
            : t("signup.create_workspace")}
        </button>
      </form>
    </div>
  );
}

function SlugStatus({
  checking,
  check,
}: {
  checking: boolean;
  check: SignupSlugCheck | null;
}) {
  const { t } = useTranslation();
  if (checking) {
    return (
      <p className="mt-1 text-xs text-slate-500">
        {t("signup.checking_availability")}
      </p>
    );
  }
  if (!check) return null;
  if (check.available) {
    return (
      <p className="mt-1 text-xs text-emerald-700">
        ✓ {t("signup.slug_available_prefix")}{" "}
        <LtrText>{check.slug}.solvait.ai</LtrText>
      </p>
    );
  }
  return (
    <p className="mt-1 text-xs text-rose-700">
      ✗ {check.reason ?? t("signup.slug_not_available")}
    </p>
  );
}
